// @ts-nocheck
import Vue from 'vue';
import { FIELD_MAPPINGS, GEM_THEME_COLORS, getDynamicGemThemeColor, FALLBACK_THEME_COLORS } from '@/config/defaultGemTypes';
import { store } from '@/stores';
import { themeStore } from '@/stores/themeStore';
export default Vue.extend({
    name: 'DynamicGemTypeCard',
    props: {
        type: {
            type: String,
            required: true
        },
        items: {
            type: Array,
            required: true
        },
        config: {
            type: Object,
            required: true
        },
        isCollapsed: {
            type: Boolean,
            default: false
        },
        maxDisplayItems: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            showAllItems: false,
            hoveredItem: null
        };
    },
    computed: {
        cardClasses() {
            const hasFlashingGems = this.items.some(item => store.gems.isGemFlashing(this.type, this.getItemTitle(item)));
            // Check if the entire type is flashing (new gem type)
            const isTypeFlashing = store.gems.isGemTypeFlashing(this.type);
            // Card should flash if either individual gems are flashing OR the entire type is new
            const shouldFlash = hasFlashingGems || isTypeFlashing;
            return [
                'dynamic-gem-type-card',
                `gem-type-${this.type.toLowerCase().replace(/[^a-z0-9]/g, '-')}`,
                {
                    'collapsed': this.isCollapsed,
                    'custom-type': this.config.isCustom,
                    'has-items': this.items.length > 0,
                    'empty': this.items.length === 0,
                    'gem-card--flashing': shouldFlash
                }
            ];
        },
        cardStyles() {
            // Get theme-aware colors
            let themeColors;
            // Check if it's a default gem type
            if (GEM_THEME_COLORS[this.type]) {
                themeColors = GEM_THEME_COLORS[this.type];
            }
            else {
                // For dynamic types, use hash-based color selection
                let hash = 0;
                for (let i = 0; i < this.type.length; i++) {
                    const char = this.type.charCodeAt(i);
                    hash = ((hash << 5) - hash) + char;
                    hash = hash & hash; // Convert to 32-bit integer
                }
                const colorIndex = Math.abs(hash) % 20; // We have 20 dynamic colors
                themeColors = getDynamicGemThemeColor(colorIndex);
            }
            // Get colors based on current theme
            const currentColors = themeColors[themeStore.theme] || FALLBACK_THEME_COLORS[themeStore.theme];
            return {
                '--gem-type-color': currentColors.color,
                '--gem-type-bg': currentColors.backgroundColor,
                '--gem-type-border': currentColors.color + '40',
                '--gem-type-hover': currentColors.color + '20' // 12% opacity
            };
        },
        displayItems() {
            const maxItems = this.maxDisplayItems || this.config.maxItems || 20;
            if (this.showAllItems || this.items.length <= maxItems) {
                return this.items;
            }
            return this.items.slice(0, maxItems);
        },
        hasMoreItems() {
            const maxItems = this.maxDisplayItems || this.config.maxItems || 20;
            return !this.showAllItems && this.items.length > maxItems;
        },
        remainingCount() {
            const maxItems = this.maxDisplayItems || this.config.maxItems || 20;
            return Math.max(0, this.items.length - maxItems);
        },
        gridStyles() {
            return {
                '--items-count': this.displayItems.length
            };
        },
        indicatorStyles() {
            // Indicator uses the gem type color set in cardStyles
            return {};
        },
        footerStyles() {
            // Footer uses the gem type colors set in cardStyles
            return {};
        }
    },
    methods: {
        getItemKey(item, index) {
            // For project-level gems, include source session to ensure uniqueness
            const sourceSession = item._source_session;
            if (sourceSession && item.id) {
                return `${this.type}-${sourceSession}-${item.id}-${index}`;
            }
            // Create a unique hash from the item's content for better uniqueness
            const textContent = item.text || item.title || item.quote || '';
            let contentHash = 0;
            for (let i = 0; i < textContent.length; i++) {
                const char = textContent.charCodeAt(i);
                contentHash = ((contentHash << 5) - contentHash) + char;
                contentHash = contentHash & contentHash; // Convert to 32bit integer
            }
            // Always include type, id, content hash, and index to ensure uniqueness
            const idPart = item.id?.toString() || 'no-id';
            return `${this.type}-${idPart}-${Math.abs(contentHash)}-${index}`;
        },
        getItemTitle(item) {
            // Try multiple fields for title
            for (const field of FIELD_MAPPINGS.title) {
                if (item[field]) {
                    return item[field];
                }
            }
            return `${this.type} Item #${item.id || 'N/A'}`;
        },
        getItemOriginalTitle(item) {
            for (const field of FIELD_MAPPINGS.originalLanguage) {
                if (item[field] && item[field] !== this.getItemTitle(item)) {
                    return item[field];
                }
            }
            return null;
        },
        getItemSummary(item) {
            for (const field of FIELD_MAPPINGS.description) {
                if (item[field]) {
                    return item[field];
                }
            }
            return null;
        },
        getItemAction(item) {
            for (const field of FIELD_MAPPINGS.action) {
                if (item[field]) {
                    return item[field];
                }
            }
            return null;
        },
        getItemAriaLabel(item) {
            const title = this.getItemTitle(item);
            return `${this.config.displayName || this.type}: ${title}`;
        },
        itemClasses(item) {
            const isFlashing = store.gems.isGemFlashing(this.type, this.getItemTitle(item));
            return [
                'gem-item',
                {
                    'gem-item--flashing': isFlashing
                },
                {
                    'has-action': !!this.getItemAction(item),
                    'has-original-title': !!this.getItemOriginalTitle(item),
                    'hovered': this.hoveredItem === item
                }
            ];
        },
        handleCardClick() {
            // Only expand when collapsed
            if (this.isCollapsed && this.config.collapsible !== false) {
                this.handleToggleCollapse();
            }
        },
        handleHeaderClick() {
            // Header always toggles
            if (this.config.collapsible !== false) {
                this.handleToggleCollapse();
            }
        },
        handleToggleCollapse() {
            this.$emit('toggle-collapse', this.type);
        },
        handleItemClick(item) {
            // Emit both the legacy event and new navigation event
            this.$emit('item-click', item, this.type);
            this.$emit('item-navigate', item);
        },
        handleItemHover(item, event) {
            this.hoveredItem = item;
            if (item) {
                this.$emit('show-tooltip', item, event.currentTarget, this.type);
            }
            else {
                this.$emit('hide-tooltip');
            }
            this.$emit('item-hover', item, this.type);
        },
        handleShowMore() {
            this.showAllItems = true;
            this.$nextTick(() => {
                this.$emit('items-expanded', this.type);
            });
        }
    },
    watch: {
        isCollapsed(newValue) {
            if (!newValue && this.showAllItems) {
                this.showAllItems = false;
            }
        }
    }
});
